import React from 'react';

import { ApiOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Flex, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';

import { usePartnerStats } from '../../api/getPartnerStats';
import { usePartnerOrgBlockerView } from '../../hooks/usePartnerOrgBlockerView';
import { ArrowUpRightFromSquareIcon } from '../Icons/ArrowUpRightFromSquareIcon';
import { CheckFull } from '../Icons/CheckFull';

const { Text } = Typography;

type Warning = {
  isError?: boolean;
  prefixIcon: React.ReactNode;
  label: React.ReactNode;
  link?: React.ReactNode;
};

const LocalLink = ({ to, label, ...rest }) => (
  <Link
    to={to}
    target="_blank"
    rel="noopener noreferrer"
    {...rest}
    style={{
      color: '#000B07',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    }}
  >
    <Text underline>{label}</Text>
    <ArrowUpRightFromSquareIcon />
  </Link>
);

const ExclamationPrefixIcon = () => (
  <ExclamationCircleOutlined
    style={{
      fontSize: '1.5rem',
      color: '#FFA500',
    }}
  />
);
const CheckCirclePrefixIcon = () => (
  <CheckFull
    width="1.5rem"
    height="1.5rem"
    style={{
      color: '#00BF6F',
    }}
  />
);
const ErrorPrefixIcon = () => (
  <ApiOutlined
    style={{
      color: '#C82F21',
      fontSize: '1.5rem',
    }}
  />
);

const getStatusIcon = ({ isSuccess }: { isSuccess: boolean }) =>
  isSuccess ? <CheckCirclePrefixIcon /> : <ExclamationPrefixIcon />;

export const PartnerStatsWarnings = () => {
  const { id } = useParams<{ id: string }>();

  const { partnerState, isMasterAccount, isAssociatedToCompany, hasChildren } =
    usePartnerOrgBlockerView(id!);

  const { data: partnerStats } = usePartnerStats({});

  const searchParams = new URLSearchParams();

  Object.entries({
    partners: JSON.stringify([
      {
        label: partnerState.data.partner_id_card.client_name,
        partnerId: partnerState.data.id,
      },
    ]),
  }).forEach(([key, value]) => {
    if (value && value !== '') {
      searchParams.set(key, value);
    }
  });

  const toProductsLitigation = `/products?${searchParams.toString()}`;

  const warnings: Warning[] = [];

  if (partnerStats) {
    if (partnerStats.unpaidBills.error) {
      warnings.push({
        isError: true,
        prefixIcon: <ErrorPrefixIcon />,
        label: (
          <>
            Error retrieving <strong>Unpaid bills</strong> in Sylob
          </>
        ),
      });
    } else {
      warnings.push({
        prefixIcon: getStatusIcon({
          isSuccess: partnerStats.unpaidBills.value === 0,
        }),
        label:
          partnerStats.unpaidBills.value! > 0 ? (
            <>
              Organization has <strong>unpaid bills</strong> and is blocked in
              Sylob
            </>
          ) : (
            <>
              No <strong>bills unpaid</strong> in Sylob
            </>
          ),
      });
    }

    if (partnerStats.litigation.error) {
      warnings.push({
        isError: true,
        prefixIcon: <ErrorPrefixIcon />,
        label: (
          <>
            Error retrieving shipments in <strong>Litigation </strong>
          </>
        ),
      });
    } else {
      warnings.push({
        prefixIcon: getStatusIcon({
          isSuccess: partnerStats.litigation.value === 0,
        }),
        label:
          partnerStats.litigation.value! > 0 ? (
            <>
              {partnerStats.litigation.value} Shipment
              {partnerStats.litigation.value === 1 ? '' : 's'} in{' '}
              <strong>Litigation process</strong>
            </>
          ) : (
            <>
              No shipments in <strong>Litigation process</strong>
            </>
          ),
      });
    }

    if (partnerStats.toIsolate.error) {
      warnings.push({
        isError: true,
        prefixIcon: <ErrorPrefixIcon />,
        label: (
          <>
            Error retrieving products in <strong>IN_BUSINESS FLAGGED</strong>
          </>
        ),
      });
    } else {
      warnings.push({
        prefixIcon: getStatusIcon({
          isSuccess: partnerStats.toIsolate.value === 0,
        }),
        label:
          partnerStats.toIsolate.value! > 0 ? (
            <>
              There {partnerStats.toIsolate.value === 1 ? 'is' : 'are'}{' '}
              {partnerStats.toIsolate.value} product
              {partnerStats.toIsolate.value === 1 ? '' : 's'}{' '}
              <strong>IN_BUSINESS FLAGGED</strong>
            </>
          ) : (
            <>
              No products <strong>IN_BUSINESS FLAGGED</strong>
            </>
          ),
        link:
          partnerStats.toIsolate.value! > 0 ? (
            <LocalLink
              to={toProductsLitigation}
              label={`See product${partnerStats.toIsolate.value! > 1 ? 's' : ''}`}
            />
          ) : null,
      });
    }

    if (partnerStats.inBusiness.error) {
      warnings.push({
        isError: true,
        prefixIcon: <ErrorPrefixIcon />,
        label: <>Error retrieving products to be returned</>,
      });
    } else {
      warnings.push({
        prefixIcon: getStatusIcon({
          isSuccess: partnerStats.inBusiness.value === 0,
        }),
        label:
          partnerStats.inBusiness.value! > 0 ? (
            <>
              There {partnerStats.inBusiness.value === 1 ? 'is' : 'are'}{' '}
              {partnerStats.inBusiness.value} LP product
              {partnerStats.inBusiness.value === 1 ? '' : 's'} to be returned
            </>
          ) : (
            <>All LP products have been returned</>
          ),
        link:
          partnerStats.inBusiness.value! > 0 ? (
            <LocalLink
              to={toProductsLitigation}
              label={`See ${
                partnerStats.inBusiness.value === 1 ? '' : 'all'
              } product${partnerStats.inBusiness.value === 1 ? '' : 's'}`}
            />
          ) : null,
      });
    }
  }

  if (isMasterAccount && hasChildren) {
    warnings.push({
      prefixIcon: <ExclamationPrefixIcon />,
      label: (
        <>
          Company has <strong>child organizations</strong> — unlink before
          closing
        </>
      ),
    });
  }

  if (isAssociatedToCompany) {
    warnings.push({
      prefixIcon: <ExclamationPrefixIcon />,
      label: (
        <>
          Organization <strong>linked to a company</strong> — unlink before
          closing
        </>
      ),
    });
  }

  return (
    <Flex vertical align="start" gap=".5rem">
      {warnings.map((warning: Warning, index: number) => (
        <Flex gap="1rem" key={index} align="start">
          {warning.prefixIcon}
          <Text
            style={
              warning.isError
                ? {
                    color: '#C82F21',
                  }
                : {}
            }
          >
            {warning.label}
          </Text>
          {warning.link}
        </Flex>
      ))}
    </Flex>
  );
};
